// TabbedSection.jsx
// Two-tab layout merging "Your 10-Section Report" (primary) and
// "How It's Calculated" (secondary) into one compact section.
// Tab 1 reuses sx-depth-* styles. Tab 2 reuses sx-pipeline-* styles.
import React, { useState } from "react";

export default function TabbedSection({ tabs = [] }) {
  const [active, setActive] = useState(0);
  if (!tabs.length) return null;

  const tab = tabs[active];

  return (
    <div className="sx-container">
      <div className="sx-tabbed-wrapper">
        {/* Tab bar */}
        <div className="sx-tab-bar">
          {tabs.map((t, i) => (
            <button
              key={i}
              type="button"
              className={`sx-tab-btn${active === i ? " sx-tab-btn--active" : ""}`}
              onClick={() => setActive(i)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content — key forces fade animation on switch */}
        <div className="sx-tab-content" key={active}>
          {tab.heading && <h3 className="sx-pipeline-heading">{tab.heading}</h3>}
          {tab.subheading && <p className="sx-pipeline-sub">{tab.subheading}</p>}

          {/* ── Tab type: 10-section report grid ── */}
          {tab.type === "report_chapters" && (
            <div className="sx-depth-grid">
              {(tab.chapters || []).map((ch, i) => (
                <div key={i} className="sx-depth-card">
                  <div className="sx-depth-card-left">
                    <div className="sx-depth-card-num">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="sx-depth-card-icon">{ch.icon}</div>
                  </div>
                  <div className="sx-depth-card-body">
                    <div className="sx-depth-card-title">{ch.title}</div>
                    <p className="sx-depth-card-desc">{ch.desc}</p>
                    {ch.tags && ch.tags.length > 0 && (
                      <div className="sx-depth-card-tags">
                        {ch.tags.map((tag, j) => (
                          <span key={j} className="sx-depth-tag">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Tab type: 4-engine pipeline ── */}
          {tab.type === "pipeline" && (
            <div className="sx-pipeline-steps">
              {(tab.steps || []).map((step, i) => (
                <React.Fragment key={i}>
                  <div className="sx-pipeline-step">
                    <div className="sx-pipeline-step-num">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="sx-pipeline-step-icon">{step.icon}</div>
                    <div className="sx-pipeline-step-title">{step.title}</div>
                    <div className="sx-pipeline-step-body">{step.body}</div>
                    {step.tags && step.tags.length > 0 && (
                      <div className="sx-pipeline-step-tags">
                        {step.tags.map((tag, j) => (
                          <span key={j} className="sx-pipeline-tag">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {i < (tab.steps?.length ?? 0) - 1 && (
                    <div className="sx-pipeline-arrow">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M3 8h10M9 4l4 4-4 4" stroke="rgba(148,163,184,0.5)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {tab.note && <p className="sx-insight-note">{tab.note}</p>}
        </div>
      </div>
    </div>
  );
}
